import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Empty } from "./OverviewTab";
import { FloppyDisk, FilePdf, CheckCircle, Info, Plus, Trash, Calendar } from "@phosphor-icons/react";
import { downloadBlob, downloadJsonFile } from "../../lib/downloads";

const QUARTERS = ["annual", "Q1", "Q2", "Q3", "Q4"];

const EMPTY = {
  year: new Date().getFullYear(),
  quarter: "annual",
  review_period: "",
  next_review_date: "",
  responsible: "",
  data_sources: [],
  indicators: "",
  complaints_summary: "",
  trend_analysis: "",
  capas_summary: "",
  conclusions: "",
};

function getPsurApplicability(devices) {
  if (!devices?.length || devices.some((device) => !device.mdr_class)) {
    return { eligible: false, messageKey: "pms.psur_class_missing" };
  }
  const classes = new Set(devices.map((device) =>
    ["I", "Is", "Im", "Ir"].includes(device.mdr_class) ? "I" : device.mdr_class
  ));
  if (classes.size > 1) return { eligible: false, messageKey: "pms.psur_mixed_classes" };
  const mdrClass = [...classes][0];
  if (mdrClass === "I") return { eligible: false, messageKey: "pms.psur_class_i" };
  if (!["IIa", "IIb", "III"].includes(mdrClass)) {
    return { eligible: false, messageKey: "pms.psur_other_class" };
  }
  return { eligible: true, mdrClass };
}

export default function PmsTab({ project, version, devices = [], deviceId }) {
  const t = useT();
  const [records, setRecords] = useState([]);
  const [traceRecords, setTraceRecords] = useState([]);
  const [qmsRecords, setQmsRecords] = useState([]);
  const [activeKey, setActiveKey] = useState(null); // `${year}-${quarter}`
  const [draft, setDraft] = useState(EMPTY);
  const [sourcesText, setSourcesText] = useState("");
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [downloading, setDownloading] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const psurApplicability = useMemo(() => getPsurApplicability(devices), [devices]);

  const load = useCallback(async (selectKey = null) => {
    const params = deviceId ? { device_id: deviceId } : {};
    const [r, trace, ...qms] = await Promise.all([
      api.get(`/projects/${project.id}/pms`, { params }),
      api.get(`/projects/${project.id}/trace`, { params }).catch(() => ({ data: [] })),
      ...["complaint", "ncr", "capa", "recall", "fsca"].map((type) =>
        api.get(`/projects/${project.id}/qms/${type}`, { params }).catch(() => ({ data: [] }))
      ),
    ]);
    const list = r.data || [];
    setRecords(list);
    setTraceRecords(trace.data || []);
    setQmsRecords(qms.flatMap((response) => response.data || []));
    const target = selectKey
      ? list.find((x) => `${x.year}-${x.quarter}` === selectKey)
      : list[0];
    if (target) {
      setActiveKey(`${target.year}-${target.quarter}`);
      setDraft({ ...EMPTY, ...target });
      setSourcesText((target.data_sources || []).join("\n"));
    } else {
      setActiveKey(null);
      setDraft(EMPTY);
      setSourcesText("");
    }
  }, [project?.id, deviceId]);

  useEffect(() => { if (project?.id) load(); }, [project?.id, load]);

  function newPeriod() {
    setActiveKey(null);
    setDraft(EMPTY);
    setSourcesText("");
    setOkMsg("");
  }

  function selectRecord(rec) {
    setActiveKey(`${rec.year}-${rec.quarter}`);
    setDraft({ ...EMPTY, ...rec });
    setSourcesText((rec.data_sources || []).join("\n"));
    setOkMsg("");
  }

  async function save() {
    setSaving(true);
    setOkMsg("");
    try {
      const payload = {
        device_id: deviceId,
        year: parseInt(draft.year, 10),
        quarter: draft.quarter,
        review_period: draft.review_period,
        next_review_date: draft.next_review_date,
        responsible: draft.responsible,
        indicators: draft.indicators,
        complaints_summary: draft.complaints_summary,
        trend_analysis: draft.trend_analysis,
        capas_summary: draft.capas_summary,
        conclusions: draft.conclusions,
        data_sources: sourcesText.split("\n").map((s) => s.trim()).filter(Boolean),
        source_trace_ids: draft.source_trace_ids || [],
        source_qms_ids: draft.source_qms_ids || [],
      };
       await api.put(`/projects/${project.id}/pms`, payload);
      setOkMsg(t("pms.saved"));
      await load(`${payload.year}-${payload.quarter}`);
    } finally {
      setSaving(false);
    }
  }

  async function removeRecord(rec) {
    if (!window.confirm(t("pms.confirm_delete"))) return;
    await api.delete(`/projects/${project.id}/pms/${rec.id}`);
    await load();
  }

  async function downloadPdf(kind, rec = null) {
    setDownloading(`${kind}-${rec ? rec.id : ""}`);
    setDownloadError("");
    try {
      const lang = localStorage.getItem("rg_lang") || "en";
      let path;
      let filename;
      if (kind === "pms") {
        path = `/projects/${project.id}/pms/pdf?year=${rec.year}&quarter=${rec.quarter}&lang=${lang}${deviceId ? `&device_id=${encodeURIComponent(deviceId)}` : ""}`;
        filename = `RA-PMS_${project.id.slice(0, 8)}_${rec.year}_${rec.quarter}.pdf`;
      } else if (kind === "psur") {
        path = `/projects/${project.id}/psur/pdf?lang=${lang}${deviceId ? `&device_id=${encodeURIComponent(deviceId)}` : ""}`;
        filename = `RA-PSUR_${project.id.slice(0, 8)}_${new Date().getFullYear()}.pdf`;
      } else {
        path = `/projects/${project.id}/versions/${version.version}/capas/pdf?lang=${lang}${deviceId ? `&device_id=${encodeURIComponent(deviceId)}` : ""}`;
        filename = `RA-CAPA_${project.id.slice(0, 8)}_v${version.version}.pdf`;
      }
      const res = await fetch(`${API}${path}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rg_token")}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setDownloadError(error.message || t("pms.download_error"));
    } finally {
      setDownloading("");
    }
  }

  async function downloadJson(kind, rec = null) {
    setDownloadError("");
    if (kind === "psur") {
      setDownloading("psur-json");
      try {
        const lang = localStorage.getItem("rg_lang") || "en";
        const res = await fetch(`${API}/projects/${project.id}/psur/json`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("rg_token")}` },
          credentials: "include",
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || t("pms.download_error"));
        }
        downloadBlob(await res.blob(), `RA-PSUR-SOURCE_${project.id.slice(0, 8)}_${lang}.json`);
      } catch (error) {
        setDownloadError(error.message || t("pms.download_error"));
      } finally {
        setDownloading("");
      }
      return;
    }
    if (kind === "pms" && rec) {
      downloadJsonFile({
        schema: "airegulatory.pms-period-source.v1",
        document_type: "pms_period_source_data",
        exported_at: new Date().toISOString(),
        project: { id: project.id, name: project.name },
        mdr_classes_recorded: [...new Set(devices.map((device) => device.mdr_class).filter(Boolean))],
        record: rec,
        limitations: ["Datos guardados para revisión humana; no constituyen una evaluación clínica ni una conclusión regulatoria."],
      }, `RA-PMS_${project.id.slice(0, 8)}_${rec.year}_${rec.quarter}.json`);
      return;
    }
    if (kind === "capa" && version) {
      downloadJsonFile({
        schema: "airegulatory.capa-source.v1",
        document_type: "capa_register_source_data",
        exported_at: new Date().toISOString(),
        project: { id: project.id, name: project.name },
        version: version.version,
        compliance_gaps: version.compliance_gaps || [],
        limitations: ["Registro de acciones asociado a brechas; la exportación no declara eficacia ni cierre automático."],
      }, `RA-CAPA_${project.id.slice(0, 8)}_v${version.version}.json`);
    }
  }

  function exportPmsHistoryJson() {
    downloadJsonFile({
      schema: "airegulatory.pms-history-source.v1",
      document_type: "pms_history_source_data",
      exported_at: new Date().toISOString(),
      project: { id: project.id, name: project.name },
      mdr_classes_recorded: [...new Set(devices.map((device) => device.mdr_class).filter(Boolean))],
      records,
      limitations: ["Datos de proyecto guardados en la aplicación; revise su aplicabilidad y suficiencia antes de utilizarlos."],
    }, `RA-PMS-HISTORY_${project.id.slice(0, 8)}.json`);
  }

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return [y - 2, y - 1, y, y + 1, y + 2];
  }, []);

  return (
    <div className="space-y-5" data-testid="pms-tab">
      <div className="grid-card p-5 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-extrabold tracking-tight">{t("pms.title")}</h2>
          <p className="text-sm text-zinc-600 mt-1">{t("pms.sub")}</p>
          <p className="mt-2 text-xs text-[var(--text-2)]" data-testid="pms-psur-applicability">
            {psurApplicability.eligible
              ? `${t("pms.psur_class_prefix")} ${psurApplicability.mdrClass}.`
              : t(psurApplicability.messageKey)}
          </p>
          {downloadError && <p className="mt-2 text-xs text-[var(--risk-danger)]" role="alert">{downloadError}</p>}
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          <button type="button" onClick={exportPmsHistoryJson} disabled={!records.length}
            data-testid="pms-export-history-json"
            className="rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs font-semibold text-[var(--text)] hover:bg-[var(--surface-hover)] disabled:opacity-40">
            {t("exports.download_history_json")}
          </button>
          {psurApplicability.eligible && (
            <>
              <button onClick={() => downloadPdf("psur")} disabled={downloading.startsWith("psur") || records.length === 0}
                data-testid="pms-download-psur"
                className="inline-flex items-center gap-2 border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-800 px-3 py-2 rounded-sm text-xs font-semibold transition-colors disabled:opacity-40">
                <FilePdf size={14} weight="bold" /> {downloading.startsWith("psur") ? "…" : t("pms.psur_btn")}
              </button>
              <button type="button" onClick={() => downloadJson("psur")} disabled={downloading.startsWith("psur") || records.length === 0}
                data-testid="pms-download-psur-json"
                className="rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs font-semibold text-[var(--text)] hover:bg-[var(--surface-hover)] disabled:opacity-40">
                JSON
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* History column */}
        <div className="grid-card p-4 md:col-span-1">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1">
              <Calendar size={12} /> {t("pms.history")}
            </div>
            <button onClick={newPeriod} data-testid="pms-new" className="btn-primary inline-flex items-center gap-1 px-2 py-1 text-xs">
              <Plus size={12} weight="bold" /> {t("pms.new_period")}
            </button>
          </div>
          {records.length === 0 ? (
            <div className="text-xs text-zinc-500 font-mono py-4 text-center">{t("pms.no_records")}</div>
          ) : (
            <ul className="space-y-1.5" data-testid="pms-records">
              {records.map((rec) => {
                const key = `${rec.year}-${rec.quarter}`;
                const isActive = key === activeKey;
                return (
                  <li key={rec.id}
                    className={`flex items-center justify-between border rounded-sm px-2 py-1.5 text-xs cursor-pointer transition-colors ${isActive ? "border-blue-500 bg-blue-50" : "border-zinc-200 hover:bg-zinc-50"}`}
                    onClick={() => selectRecord(rec)}
                    data-testid={`pms-record-${key}`}>
                    <div className="font-mono">
                      <span className="font-bold">{rec.year}</span>
                      <span className="mx-1 text-zinc-400">·</span>
                      <span>{rec.quarter}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={(e) => { e.stopPropagation(); downloadPdf("pms", rec); }}
                        disabled={downloading.startsWith(`pms-${rec.id}`)}
                        title={t("pms.download_pms")}
                        data-testid={`pms-dl-${key}`}
                        className="p-1 hover:bg-blue-100 text-blue-700 rounded-sm">
                        <FilePdf size={14} weight="bold" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); downloadJson("pms", rec); }}
                        title={t("exports.download_json")}
                        data-testid={`pms-json-${key}`}
                        className="rounded-sm p-1 text-[var(--text)] hover:bg-[var(--surface-hover)]">
                        <span className="font-mono text-[9px] font-bold">JSON</span>
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); removeRecord(rec); }}
                        title={t("pms.delete")}
                        data-testid={`pms-del-${key}`}
                        className="p-1 hover:bg-red-100 text-red-700 rounded-sm">
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Editor column */}
        <div className="grid-card p-4 md:col-span-2 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase text-zinc-600">{t("pms.year")}</label>
              <select value={draft.year} onChange={(e) => setDraft({ ...draft, year: e.target.value })}
                data-testid="pms-year"
                className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm bg-white focus:border-blue-600 focus:outline-none">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-zinc-600">{t("pms.quarter")}</label>
              <select value={draft.quarter} onChange={(e) => setDraft({ ...draft, quarter: e.target.value })}
                data-testid="pms-quarter"
                className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm bg-white focus:border-blue-600 focus:outline-none">
                {QUARTERS.map((q) => <option key={q} value={q}>{q === "annual" ? t("pms.q_annual") : q}</option>)}
              </select>
            </div>
            <Field label={t("pms.review_period")} value={draft.review_period} onChange={(v) => setDraft({ ...draft, review_period: v })} testid="pms-period" placeholder="Annual / 6 months" />
            <Field label={t("pms.next_review")} type="date" value={draft.next_review_date} onChange={(v) => setDraft({ ...draft, next_review_date: v })} testid="pms-next" />
            <Field label={t("pms.responsible")} value={draft.responsible} onChange={(v) => setDraft({ ...draft, responsible: v })} testid="pms-responsible" />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase text-zinc-600">{t("pms.data_sources")}</label>
            <textarea data-testid="pms-sources" value={sourcesText} onChange={(e) => setSourcesText(e.target.value)}
              rows={3} placeholder={t("pms.data_sources_hint")}
              className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-semibold text-[var(--text-2)]">
              {t("pms.link_trace_sources")}
              <select
                multiple
                value={draft.source_trace_ids || []}
                onChange={(event) => setDraft({ ...draft, source_trace_ids: Array.from(event.target.selectedOptions, (option) => option.value) })}
                data-testid="pms-link-trace"
                className="mt-1 min-h-28 w-full rounded-sm border border-[var(--border)] bg-[var(--surface)] p-2 text-sm text-[var(--text)]"
              >
                {traceRecords.map((record) => <option key={record.id} value={record.id}>{record.ref} · {record.batch_lot || record.variant_name || record.product}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-[var(--text-2)]">
              {t("pms.link_qms_sources")}
              <select
                multiple
                value={draft.source_qms_ids || []}
                onChange={(event) => setDraft({ ...draft, source_qms_ids: Array.from(event.target.selectedOptions, (option) => option.value) })}
                data-testid="pms-link-qms"
                className="mt-1 min-h-28 w-full rounded-sm border border-[var(--border)] bg-[var(--surface)] p-2 text-sm text-[var(--text)]"
              >
                {qmsRecords.map((record) => <option key={record.id} value={record.id}>{record.ref} · {record.title}</option>)}
              </select>
            </label>
            <p className="text-xs text-[var(--text-2)] sm:col-span-2">{t("pms.link_sources_note")}</p>
          </div>

          <Area label={t("pms.indicators")} value={draft.indicators} onChange={(v) => setDraft({ ...draft, indicators: v })} testid="pms-indicators" />
          <Area label={t("pms.complaints")} value={draft.complaints_summary} onChange={(v) => setDraft({ ...draft, complaints_summary: v })} testid="pms-complaints" />
          <Area label={t("pms.trends")} value={draft.trend_analysis} onChange={(v) => setDraft({ ...draft, trend_analysis: v })} testid="pms-trends" />
          <Area label={t("pms.capas")} value={draft.capas_summary} onChange={(v) => setDraft({ ...draft, capas_summary: v })} testid="pms-capas" />
          <Area label={t("pms.conclusions")} value={draft.conclusions} onChange={(v) => setDraft({ ...draft, conclusions: v })} testid="pms-conclusions" />

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-zinc-100">
            <button onClick={save} disabled={saving} data-testid="pms-save" className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm">
              <FloppyDisk size={14} weight="bold" /> {saving ? "…" : t("pms.save")}
            </button>
            <button onClick={() => downloadPdf("capa")} disabled={!version || downloading.startsWith("capa")} data-testid="pms-download-capa"
              className="inline-flex items-center gap-2 border border-emerald-200 hover:bg-emerald-50 hover:border-emerald-400 text-emerald-700 px-4 py-2 rounded-sm text-sm font-semibold transition-colors disabled:opacity-40">
              <FilePdf size={14} weight="bold" /> {downloading.startsWith("capa") ? "…" : t("pms.download_capa")}
            </button>
            <button type="button" onClick={() => downloadJson("capa")} disabled={!version}
              data-testid="pms-download-capa-json"
              className="rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs font-semibold text-[var(--text)] hover:bg-[var(--surface-hover)] disabled:opacity-40">
              JSON
            </button>
            {okMsg && (
              <span className="inline-flex items-center gap-1 text-sm text-emerald-700">
                <CheckCircle size={14} weight="fill" /> {okMsg}
              </span>
            )}
          </div>

          <div className="text-xs text-zinc-500 inline-flex items-start gap-1">
            <Info size={12} className="mt-0.5" /> {t("pms.capa_help")}
          </div>
        </div>
      </div>

      {!version && <Empty msg={t("ov.no_analysis")} />}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-zinc-600">{label}</label>
      <input type={type} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
    </div>
  );
}

function Area({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-zinc-600">{label}</label>
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full border border-zinc-200 px-2 py-1.5 rounded-sm text-sm focus:border-blue-600 focus:outline-none" />
    </div>
  );
}
